import React, { useContext, useRef, useState, useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { graphql } from 'react-relay';
import type { ComponentType } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { Help } from '@atlaskit/navigation-system/top-nav-items';

import { useRightSidebarContext } from '@confluence/page-layout-context';
import { useInProductHelpActions } from '@confluence/in-product-help/entry-points/InProductHelpProvider.ts';
import { getRouteData } from '@confluence/in-product-help/entry-points/inProductHelpUtil.ts';
import {
	HELP_PANEL_ID,
	HELP_PANEL_WIDTH,
} from '@confluence/in-product-help/entry-points/constants.ts';
import { START_TOUCH } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import { VIEW_IN_PRODUCT_HELP_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState.ts';
import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import {
	HELP_SHORTCUT,
	UNIVERSAL_HELP_SHORTCUT,
	UNIVERSAL_OPEN_DEVTOOLS_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { KeyboardShortcutsDialog } from '@confluence/keyboard-shortcuts-dialog';
import { preloadHelpMenu } from '@confluence/in-product-help/entry-points/preloadHelpMenu.ts';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { isAtlassianStaff } from '@confluence/in-product-help/entry-points/isAtlassianStaff.ts';
import type { DialogsContainerProps } from '@confluence/dialogs/entry-points/DialogsStateContainer.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { DevToolsDialog } from '@confluence/dev-tools';

import type { HelpButtonQuery$data } from './__generated__/HelpButtonQuery.graphql.ts';

const HelpButtonQuery = graphql`
	query HelpButtonQuery {
		user: confluence_atlassianUser(current: true) {
			emails {
				value
			}
		}
	}
`;

export const HelpButton = () => {
	const triggerButtonRef = useRef<HTMLButtonElement | null>(null);
	const rightSidebarContext = useRightSidebarContext();
	const inProductHelp = useInProductHelpActions();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const routeData = useRouteDataRef();
	const intl = useIntl();
	const isHelpPanelOpen = rightSidebarContext.getCurrent()?.panelId === HELP_PANEL_ID;
	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();
	const isOnEditRoute = useIsEditorPage();
	const { showModal } = useDialogs();

	const { data } = useQueryInterop<HelpButtonQuery$data>(HelpButtonQuery);
	const isInternalAtlassianUser = useMemo(() => isAtlassianStaff(data), [data]);

	const [activeDialog, setActiveDialog] = useState(null);

	const onClick = () => {
		if (isHelpPanelOpen) {
			rightSidebarContext.close(HELP_PANEL_ID);
		} else {
			experienceTracker.start({
				name: VIEW_IN_PRODUCT_HELP_EXPERIENCE,
				attributes: {
					navVersion: '4',
				},
			});

			rightSidebarContext.open(HELP_PANEL_ID, HELP_PANEL_WIDTH);
			rightSidebarContext.setTriggerButtonRef?.(triggerButtonRef);

			const { routeName, routeGroup } = getRouteData(routeData.current?.href || '');
			inProductHelp.setRouteName(routeName);
			inProductHelp.setRouteGroup(routeGroup);
		}

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'globalNavigation',
				action: 'clicked',
				actionSubject: 'navigationItem',
				actionSubjectId: 'help',
				attributes: {
					selectedItemPageContext: routeData.current?.routeName,
					navigationLayer: 'global',
					navigationContainer: 'top',
					navVersion: '4',
					navdexPointType: START_TOUCH,
				},
			},
		}).fire();
	};

	const closeDialog = useCallback(() => {
		setActiveDialog(null);
	}, [setActiveDialog]);

	const openDialog = useCallback(
		(dialog: any) => {
			setActiveDialog(dialog(closeDialog));
		},
		[setActiveDialog, closeDialog],
	);

	const openShortcutsDialog = () =>
		openDialog((onClose) => <KeyboardShortcutsDialog onClose={onClose} />);

	const openDevToolsDialog = () => {
		showModal(DevToolsDialog as ComponentType<DialogsContainerProps>, {});
	};

	const preloadMenu = () => {
		void preloadHelpMenu();
	};

	return (
		<>
			<div onMouseEnter={preloadMenu} onFocus={preloadMenu}>
				<Help
					onClick={onClick}
					label={intl.formatMessage(i18n.help)}
					isSelected={isHelpPanelOpen}
					ref={triggerButtonRef}
				/>
			</div>
			<GeneralShortcutListener
				accelerator={
					isLiveEditMode || isOnEditRoute
						? UNIVERSAL_HELP_SHORTCUT
						: [HELP_SHORTCUT, UNIVERSAL_HELP_SHORTCUT]
				}
				listener={openShortcutsDialog}
				isAppNavigationShortcut
			/>
			{isInternalAtlassianUser && (
				<GeneralShortcutListener
					accelerator={UNIVERSAL_OPEN_DEVTOOLS_SHORTCUT}
					listener={openDevToolsDialog}
					isAppNavigationShortcut
					allowInDialog
				/>
			)}
			<ModalTransition>{activeDialog}</ModalTransition>
		</>
	);
};

const i18n = defineMessages({
	help: {
		id: 'app-navigation.top-navigation.help',
		defaultMessage: 'Help',
		description: 'Label for the top navigation help button',
	},
});
